import type { SelectItemType } from '@orn/shared-web/components/Select';

/**
 * Pure helpers for {@link AdminSelectUsers}. Kept separate from the
 * component so the chip-mapping and reconcile-delta logic — the
 * bug-prone parts of a multi-select seeded from async-loaded ids — can
 * be unit-tested without an Apollo / react-aria render harness.
 */

/** The user shape the picker needs to render a chip / option. */
export interface IGateUser {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
}

/** Maps a loaded user to a combobox option / chip (email as label, name as supporting text). */
export function userToSelectItem(user: IGateUser): SelectItemType {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return { id: user.id, label: user.email, supportingText: name || undefined };
}

/**
 * Builds the chip for a selected id. Falls back to an `unknown user`
 * chip when the id has no loaded user (a deleted / merged account) so
 * the operator can still see and remove a stale entry rather than it
 * silently vanishing.
 */
export function buildChipItem(id: string, usersById: Map<string, IGateUser>): SelectItemType {
  const user = usersById.get(id);
  if (user) {
    return userToSelectItem(user);
  }
  return { id, label: `unknown user: ${shortId(id)}` };
}

/**
 * Computes the minimal append / remove delta to bring the current chip
 * ids in line with the desired `wantIds`. Applying only the delta (vs a
 * full re-seed) keeps input focus and avoids a re-seed loop: a user edit
 * drives the parent value, which yields an empty delta here. `toAppend`
 * preserves `wantIds` order.
 */
export function reconcileChipDelta(
  currentIds: string[],
  wantIds: string[]
): { toAppend: string[]; toRemove: string[] } {
  const want = new Set(wantIds);
  const have = new Set(currentIds);
  return {
    toAppend: wantIds.filter((id) => !have.has(id)),
    toRemove: currentIds.filter((id) => !want.has(id)),
  };
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}
